'use client';

import {
  Mail,
  CalendarDays,
  CalendarRange,
  BellOff,
  Check,
  type LucideIcon,
} from 'lucide-react';
import {
  NotificationFrequency,
  DocumentType,
} from '@paeffgen/react/crm-documents/data-access-graphql';

interface NotificationPrefsValue {
  frequency: NotificationFrequency;
  docTypes: DocumentType[];
}

interface NotificationPrefsFormProps {
  frequency: NotificationFrequency;
  docTypes: DocumentType[];
  disabled?: boolean;
  onChange: (next: NotificationPrefsValue) => void;
}

interface FrequencyOption {
  label: string;
  description: string;
  icon: LucideIcon;
}

const FREQUENCY_OPTIONS: Record<NotificationFrequency, FrequencyOption> = {
  [NotificationFrequency.INSTANT]: {
    label: 'Bei jedem Beleg',
    description: 'Sofort eine E-Mail pro neuem Beleg.',
    icon: Mail,
  },
  [NotificationFrequency.DAILY]: {
    label: 'Tägliche Zusammenfassung',
    description: 'Einmal täglich gebündelt.',
    icon: CalendarDays,
  },
  [NotificationFrequency.WEEKLY]: {
    label: 'Wöchentliche Zusammenfassung',
    description: 'Einmal pro Woche gebündelt.',
    icon: CalendarRange,
  },
  [NotificationFrequency.NONE]: {
    label: 'Keine E-Mails',
    description: 'Keine Benachrichtigungen erhalten.',
    icon: BellOff,
  },
};

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  [DocumentType.RECHNUNG]: 'Rechnungen',
  [DocumentType.GUTSCHRIFT]: 'Gutschriften',
  [DocumentType.ANGEBOT]: 'Angebote',
  [DocumentType.AUFTRAG]: 'Aufträge',
  [DocumentType.LIEFERSCHEIN]: 'Lieferscheine',
};

const SECTION_LABEL =
  'block text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3';

/**
 * Controlled, presentational Benachrichtigungseinstellungen-Formular
 * (Häufigkeit + Belegarten). Führt selbst keine Datenabfrage/-mutation aus —
 * der Aufrufer hält den State und reicht ihn über `onChange` durch.
 *
 * Häufigkeit als auswählbare Karten, Belegarten als Toggle-Chips — visuell an
 * die App-Muster (DocumentFilters-Kacheln, Material-Anfrage-Formular)
 * angelehnt. Die Controls bleiben native `<input>`-Elemente (per `sr-only`
 * versteckt, Styling über `group-has-[:checked]:` am umschließenden `<label>`),
 * damit sie zugänglich und testbar bleiben.
 */
export function NotificationPrefsForm({
  frequency,
  docTypes,
  disabled,
  onChange,
}: NotificationPrefsFormProps) {
  const toggleDocType = (docType: DocumentType) => {
    const next = docTypes.includes(docType)
      ? docTypes.filter((t) => t !== docType)
      : [...docTypes, docType];
    onChange({ frequency, docTypes: next });
  };

  return (
    <div className="space-y-6">
      <fieldset disabled={disabled}>
        <legend className={SECTION_LABEL}>Häufigkeit</legend>
        <div className="space-y-2">
          {Object.values(NotificationFrequency).map((value) => {
            const option = FREQUENCY_OPTIONS[value];
            const Icon = option.icon;
            const descId = `notification-frequency-${value}-desc`;
            return (
              <label
                key={value}
                className="group block cursor-pointer has-[:disabled]:cursor-not-allowed"
              >
                <input
                  type="radio"
                  name="notification-frequency"
                  value={value}
                  checked={frequency === value}
                  disabled={disabled}
                  aria-label={option.label}
                  aria-describedby={descId}
                  onChange={() => onChange({ frequency: value, docTypes })}
                  className="sr-only"
                />
                <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 transition-colors hover:bg-gray-50 group-has-[:checked]:border-primary group-has-[:checked]:bg-primary/10 group-has-[:focus-visible]:ring-2 group-has-[:focus-visible]:ring-primary/30 group-has-[:disabled]:opacity-60 group-has-[:disabled]:hover:bg-white">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gray-100 text-gray-500 transition-colors group-has-[:checked]:bg-primary group-has-[:checked]:text-white">
                    <Icon size={18} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-gray-900">
                      {option.label}
                    </span>
                    <span id={descId} className="block text-xs text-gray-500">
                      {option.description}
                    </span>
                  </span>
                  <Check
                    size={18}
                    aria-hidden="true"
                    className="shrink-0 text-primary opacity-0 transition-opacity group-has-[:checked]:opacity-100"
                  />
                </div>
              </label>
            );
          })}
        </div>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend className={SECTION_LABEL}>Belegarten</legend>
        <div className="flex flex-wrap gap-2">
          {Object.values(DocumentType).map((value) => {
            const label = DOC_TYPE_LABELS[value];
            return (
              <label
                key={value}
                className="group cursor-pointer has-[:disabled]:cursor-not-allowed"
              >
                <input
                  type="checkbox"
                  value={value}
                  checked={docTypes.includes(value)}
                  disabled={disabled}
                  aria-label={label}
                  onChange={() => toggleDocType(value)}
                  className="sr-only"
                />
                <span className="inline-flex items-center rounded-full border border-gray-300 bg-white px-3.5 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 group-has-[:checked]:border-primary group-has-[:checked]:bg-primary group-has-[:checked]:text-white group-has-[:focus-visible]:ring-2 group-has-[:focus-visible]:ring-primary/30 group-has-[:disabled]:opacity-60 group-has-[:disabled]:hover:bg-white">
                  {label}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
}
